/**
 * PIKA-826 — the notice shown when a committed move takes a date off a weekend
 * the Timeline is hiding.
 *
 * `moveItem` snaps a weekend boundary onto a working day and writes that snap to
 * the stored date. The snap is intended, because the Timeline counts in working
 * days, but with the weekend column absent there is nothing on screen to compare
 * against, so a start or deadline can change without a single pixel moving. The
 * weekend notice from PIKA-780 recounts afterwards, yet it only shows the task
 * leaving its list -- it never says which dates were written.
 *
 * Kept out of `canvas_timeline.js` so the decision is testable: that module
 * cannot be imported under test, which is why its own suite is skipped.
 */
import {getMomentFromCanvasTimelineDate} from './canvasTimelinePrimitives';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';
import {hasSnappedOffHiddenWeekend} from './weekendTimelineGeometry';

/** "Mon 26 Oct" — the format the weekend notice already uses for a task's real date. */
const SNAP_DATE_FORMAT = 'ddd D MMM';

/** Dates for the snap message, already formatted for display. */
export type WeekendSnapNotice = {
	startDate: string;
	endDate: string;
};

type GestureStartDates = {
	startDate?: number;
	endDate?: number;
};

type MovedItem = {
	startDate: number;
	endDate: number;
	itemType?: number;
};

/**
 * The dates to name, or null when there is nothing honest to say.
 *
 * Null covers three cases:
 *
 *  - the gesture recorded no starting dates, so there is nothing to compare;
 *  - no boundary came off a hidden weekend;
 *  - the gesture was a resize. That puts the edge on the day the user dragged it
 *    to and the bar moves with it, so the new date is already on screen, and
 *    crediting the system with it would be wrong.
 *
 * Item type is deliberately NOT one of them: every bar snaps the same way, and
 * which items qualify is the caller's decision, because only the caller knows
 * whether the move is already stored. Every caller sits at the point its own
 * move is stored: tasks in task_item, phase and project bars in EventManager,
 * each after any confirmation modal has been answered.
 */
export const getWeekendSnapNotice = (
	gestureStart: GestureStartDates,
	item: MovedItem,
	options: {hideWeekends: boolean; isWholeItemMove: boolean}
): WeekendSnapNotice | null => {
	const {startDate, endDate} = gestureStart;

	if (startDate === undefined || endDate === undefined) return null;
	if (!options.isWholeItemMove) return null;

	const before = {startDate, endDate};
	const after = {startDate: item.startDate, endDate: item.endDate};

	if (!hasSnappedOffHiddenWeekend(before, after, options.hideWeekends)) return null;

	return {
		startDate: getMomentFromCanvasTimelineDate(after.startDate).format(SNAP_DATE_FORMAT),
		endDate: getMomentFromCanvasTimelineDate(after.endDate).format(SNAP_DATE_FORMAT),
	};
};

/** The message shown, and how long it stays up. */
const SNAP_MESSAGE_ID = 'scheduling.weekend_dates_snapped';
const SNAP_TOAST_DURATION = 5000;

/**
 * Raises the snap notice when a committed move has one to show.
 *
 * Call this from the point the dates are actually stored, never from the end of
 * the gesture. A move can sit behind a confirmation modal -- a phase with tasks,
 * a project, or a People Scheduling task with several assignees dropped on an
 * unassigned role -- and cancelling it puts the old dates back. Announcing at
 * move end would name dates the user went on to reject (PIKA-826).
 */
export const showWeekendSnapToast = (
	gestureStart: GestureStartDates,
	item: MovedItem,
	options: {
		hideWeekends: boolean;
		isWholeItemMove: boolean;
		formatMessage: (descriptor: {id: string}, values?: Record<string, string>) => string;
	}
): boolean => {
	const notice = getWeekendSnapNotice(gestureStart, item, options);

	if (!notice) return false;

	createToast({message: options.formatMessage({id: SNAP_MESSAGE_ID}, notice), duration: SNAP_TOAST_DURATION});

	return true;
};

/** Just enough of the scheduling page for the notice to shape itself from. */
type SchedulingPageComponent = {
	timeline?: {isHideWeekendsSelected: () => boolean};
	props: {intl: {formatMessage: (descriptor: {id: string}, values?: Record<string, string>) => string}};
};

/**
 * The entry point both movers call once their dates are stored.
 *
 * Takes the page component rather than a prised-apart timeline and
 * formatMessage so each caller is a single line. That matters: both of them --
 * EventManager and task_item's move end -- sit in modules that cannot be
 * imported under test, so anything shaped there is shaped unwatched.
 *
 * `isWholeItemMove` stays the caller's to pass. Deriving it here would mean
 * importing ITEM_DRAG_POINT from canvas_timeline_util, which drags in the whole
 * canvas graph and would cost this module the testability it exists for.
 */
export const notifyWeekendSnapForGesture = (
	dragData: {initialStartDate?: number; initialEndDate?: number} | undefined,
	item: MovedItem,
	isWholeItemMove: boolean,
	pageComponent: SchedulingPageComponent
): boolean =>
	showWeekendSnapToast({startDate: dragData?.initialStartDate, endDate: dragData?.initialEndDate}, item, {
		hideWeekends: !!pageComponent.timeline?.isHideWeekendsSelected(),
		isWholeItemMove,
		formatMessage: pageComponent.props.intl.formatMessage,
	});
